import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Btn, Eyebrow } from "../components/Primitives";
import { api, fileUrl } from "../lib/api";

const FADE_UP_INIT = { opacity: 0, y: 10 };
const FADE_UP_ANIM = { opacity: 1, y: 0 };
const FADE_UP_TRANSITION = { duration: 0.6 };

export default function PublicProfile({ user, publicId, onBack }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const r = await api.publicProfile(publicId, user.session_id);
                setData(r);
            } catch (e) {
                setError(e.response?.status === 403 ? "Unlock required to view profiles." : "Profile not found.");
            }
        })();
    }, [publicId, user.session_id]);

    if (error) {
        return (
            <div className="flex-1 flex flex-col bg-cream p-10 items-center justify-center text-center" data-testid="public-profile-error">
                <p className="font-display italic text-2xl text-burgundy">{error}</p>
                <button onClick={onBack} className="mt-4 text-[11px] tracking-[0.28em] uppercase text-burgundy underline-offset-4 hover:underline">
                    ← Back
                </button>
            </div>
        );
    }
    if (!data) {
        return <div className="flex-1 flex items-center justify-center text-ink-muted">Loading…</div>;
    }

    const { profile, archetype, ideal_partner_archetype } = data;
    const lead = profile.photos?.[0]?.path
        ? (profile.photos[0].path === "external" ? profile.photos[0].url : fileUrl(profile.photos[0].path))
        : null;

    return (
        <div className="flex-1 flex flex-col bg-cream" data-testid="public-profile">
            <div className="px-6 pt-6 pb-2 flex items-center justify-between">
                <button data-testid="public-profile-back" onClick={onBack} className="tap-target text-[11px] tracking-[0.28em] uppercase text-ink-muted">
                    ← Back
                </button>
                <span className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">Profile</span>
            </div>

            {lead && (
                <div className="w-full aspect-[4/5] bg-burgundy/5 overflow-hidden">
                    <img src={lead} alt={profile.first_name} className="w-full h-full object-cover" />
                </div>
            )}

            <div className="px-7 py-6">
                <motion.div initial={FADE_UP_INIT} animate={FADE_UP_ANIM} transition={FADE_UP_TRANSITION}>
                    <Eyebrow>{profile.pronouns || ""}</Eyebrow>
                    <h1 className="mt-2 font-display text-5xl text-burgundy leading-[1.05]">
                        <span className="italic">{profile.first_name}</span>{profile.age ? `, ${profile.age}` : ""}
                    </h1>
                    <p className="mt-2 text-base text-ink-muted">
                        {profile.city}{profile.occupation ? ` · ${profile.occupation}` : ""}{profile.height_cm ? ` · ${profile.height_cm} cm` : ""}
                    </p>
                </motion.div>

                {archetype && (
                    <section className="mt-8 border border-burgundy/15 bg-cream-soft p-5" data-testid="public-archetype-card">
                        <div className="text-[10px] tracking-[0.32em] uppercase text-gold-deep">Their archetype</div>
                        <div className="mt-2 font-display text-2xl text-burgundy">{archetype.name}</div>
                        <p className="mt-2 font-display italic text-base text-ink leading-snug">{archetype.tagline}</p>
                    </section>
                )}

                {ideal_partner_archetype && (
                    <section className="mt-3 border border-burgundy/15 bg-cream p-5" data-testid="public-ideal-card">
                        <div className="text-[10px] tracking-[0.32em] uppercase text-gold-deep">Their ideal match</div>
                        <div className="mt-2 font-display text-2xl text-burgundy">{ideal_partner_archetype.name}</div>
                        <p className="mt-2 font-display italic text-base text-ink leading-snug">{ideal_partner_archetype.tagline}</p>
                    </section>
                )}

                {profile.bio && (
                    <section className="mt-8">
                        <Eyebrow>About</Eyebrow>
                        <p className="mt-3 font-display text-lg leading-relaxed text-ink">{profile.bio}</p>
                    </section>
                )}

                {profile.hoping_for && (
                    <section className="mt-6">
                        <Eyebrow>Hoping for</Eyebrow>
                        <p className="mt-3 font-display italic text-lg leading-snug text-ink">{profile.hoping_for}</p>
                    </section>
                )}

                {(profile.values || []).length > 0 && (
                    <section className="mt-6">
                        <Eyebrow>Values</Eyebrow>
                        <div className="mt-3 flex flex-wrap gap-1.5">
                            {profile.values.map(v => (
                                <span key={v} className="text-[10px] tracking-[0.22em] uppercase bg-burgundy/[0.06] text-burgundy px-2 py-0.5">{v}</span>
                            ))}
                        </div>
                    </section>
                )}

                {profile.video?.url && (
                    <section className="mt-6">
                        <Eyebrow>Video answer</Eyebrow>
                        <p className="mt-2 font-display italic text-sm text-ink-muted">{profile.video.prompt}</p>
                        <video src={fileUrl(profile.video.path)} controls className="mt-2 w-full aspect-[3/4] object-cover bg-burgundy/5 border border-burgundy/15" data-testid="public-video" />
                    </section>
                )}
            </div>

            <div className="px-6 pb-8">
                <Btn data-testid="public-message-btn">Send a note</Btn>
                <p className="mt-3 text-center text-[10px] tracking-[0.28em] uppercase text-ink-light">
                    Messaging coming soon
                </p>
            </div>
        </div>
    );
}
